import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { Inward } from './inwards.types';
import { HttpClient } from '@angular/common/http';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { environment } from 'environments/environment';
import moment from 'moment';
import { BaseRespone, PaginationResult } from 'app/core/models/common.response';
import { IOutwardInward } from 'app/layout/common/components/outward/outward-inward/outward-inward.types';
import { IInwardStatusChange } from 'app/layout/common/components/status-change/statuschange-inward/status-change-inward.types';
import { IGenerateReport } from 'app/layout/common/components/generate-report/generate-report.types';
import { CopyReport } from 'app/layout/common/components/copy-report/copy-report.types';

@Injectable({
    providedIn: 'root',
})
export class InwardsService {
    // Private
    private _inward: BehaviorSubject<Inward | null> = new BehaviorSubject(null);
    private _inwards: BehaviorSubject<Inward[] | null> = new BehaviorSubject(
        null
    );
    private _pagination: BehaviorSubject<PaginationResult | null> =
        new BehaviorSubject(null);
    private _nextJobNumber: BehaviorSubject<number | null> =
        new BehaviorSubject(null);
    private _outward: BehaviorSubject<IOutwardInward | null> =
        new BehaviorSubject(null);
    private _reportStatus: BehaviorSubject<IInwardStatusChange | null> =
        new BehaviorSubject(null);
    private _multipleReportStatus: BehaviorSubject<IInwardStatusChange | null> =
        new BehaviorSubject(null);
    /**
    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient) {}

    /**
     * Getter for inward
     */
    get inward$(): Observable<Inward> {
        return this._inward.asObservable();
    }
    get outward$(): Observable<IOutwardInward> {
        return this._outward.asObservable();
    }

    get reportStatus$(): Observable<IInwardStatusChange> {
        return this._reportStatus.asObservable();
    }

    get nextJobNumber$(): Observable<number> {
        return this._nextJobNumber.asObservable();
    }
    /**
     * Getter for inwards
     */
    get inwards$(): Observable<Inward[]> {
        return this._inwards.asObservable();
    }

    /**
     * Getter for pagination
     */
    get pagination$(): Observable<PaginationResult> {
        return this._pagination.asObservable();
    }

    /**
     * Get inwards
     */
    getInwards(
        data
    ): Observable<
        BaseRespone<{ pagination: PaginationResult; inwards: Inward[] }>
    > {
        return this._httpClient
            .post<
                BaseRespone<{ pagination: PaginationResult; inwards: Inward[] }>
            >(`${environment.baseUrl}/Inward/list`, data)
            .pipe(
                tap((response) => {
                    this._pagination.next(response.result.pagination);
                    this._inwards.next(response.result.inwards);
                })
            );
    }

    /**
     * Create inward
     */
    createInward(inward: Inward): Observable<BaseRespone<Inward>> {
        return this._httpClient
            .post<BaseRespone<Inward>>(`${environment.baseUrl}/Inward`, inward)
            .pipe(
                catchError((error) => {
                    return of(error.error);
                }),
                map((newProduct) => {
                    // Return the new product
                    return newProduct;
                })
            );
    }

    /**
     * Get board
     *
     * @param id
     */
    getInward(id: number): Observable<Inward> {
        return this._httpClient.get(`${environment.baseUrl}/inward/${id}`).pipe(
            catchError((error) => {
                return of(error.error);
            }),
            map((response) => response),
            tap((board) => this._inward.next(board.result))
        );
    }

    /**
     * Update the card
     *
     * @param id
     * @param card
     */
    updateInward(inward: Inward): Observable<BaseRespone<Inward>> {
        return this._httpClient
            .put<BaseRespone<Inward>>(`${environment.baseUrl}/inward`, inward)
            .pipe(
                map((response) => {
                    this._inward.next(response.result);
                    return response;
                }),
                catchError((error) => {
                    return of(error.error);
                })
            );
        // return this.inwards$.pipe(
        //     take(1),
        //     switchMap(inwards => this._httpClient.put<BaseRespone<Inward>>(`${environment.baseUrl}/inward`, inward).pipe(
        //         map((inwardCard) => {
        //             const index = inwards.findIndex(item => item.id === inward.id);

        //             // Update the product
        //             if (inwardCard.isSuccess) {
        //                 inwards[index] = inwardCard.result;

        //                 // Update the products
        //                 this._inwards.next(inwards);

        //                 this._inward.next(inwardCard.result);
        //             }
        //             // Return the updated card
        //             return inwardCard;
        //         }), catchError((error) => {
        //             return of(error.error);
        //         })
        //     ))
        // );
    }

    deleteInward(id: number): Observable<BaseRespone<boolean>> {
        return this.inwards$.pipe(
            take(1),
            switchMap((inwards) =>
                this._httpClient
                    .delete(`${environment.baseUrl}/inward/${id}`)
                    .pipe(
                        map((deleted: BaseRespone<boolean>) => {
                            // Find the index of the deleted product
                            const index = inwards.findIndex(
                                (item) => item.id === id
                            );

                            // Delete the product
                            inwards.splice(index, 1);

                            // Update the products
                            this._inwards.next(inwards);

                            // Return the deleted status
                            return deleted;
                        })
                    )
            )
        );
    }

    getNextJobNumber() {
        return this._httpClient
            .get(`${environment.baseUrl}/inward/GetLastInwardJobNumber`)
            .pipe(
                catchError((error) => {
                    return of(error.error);
                }),
                map((response) => response),
                tap((board) => this._nextJobNumber.next(board.result + 1))
            );
    }

    createOutward(
        outward: IOutwardInward
    ): Observable<BaseRespone<IOutwardInward>> {
        return this._httpClient
            .post<BaseRespone<IOutwardInward>>(
                `${environment.baseUrl}/Outward`,
                outward
            )
            .pipe(
                catchError((error) => {
                    return of(error.error);
                }),
                map((newProduct) => {
                    // Return the new product
                    return newProduct;
                })
            );
    }

    getOutward(id: number): Observable<BaseRespone<IOutwardInward>> {
        return this._httpClient
            .get(`${environment.baseUrl}/Outward/${id}`)
            .pipe(
                catchError((error) => {
                    return of(error.error);
                }),
                map((response) => response),
                tap((board) => this._outward.next(board.result))
            );
    }

    updateOutward(
        outward: IOutwardInward
    ): Observable<BaseRespone<IOutwardInward>> {
        return this._httpClient
            .put<BaseRespone<IOutwardInward>>(
                `${environment.baseUrl}/Outward`,
                outward
            )
            .pipe(
                map((response) => {
                    this._outward.next(response.result);
                    return response;
                }),
                catchError((error) => {
                    return of(error.error);
                })
            );
    }
    getReportStatusChange(
        id: number
    ): Observable<BaseRespone<IInwardStatusChange>> {
        return this._httpClient
            .get(`${environment.baseUrl}Inward/StatusChange/${id}`)
            .pipe(
                catchError((error) => {
                    return of(error.error);
                }),
                map((response) => response),
                tap((board) => this._reportStatus.next(board.result))
            );
    }
    ReportStatusChange(
        statusChange: IInwardStatusChange
    ): Observable<BaseRespone<IInwardStatusChange>> {
        return this._httpClient
            .put<BaseRespone<IInwardStatusChange>>(
                `${environment.baseUrl}/Inward/StatusChange`,
                statusChange
            )
            .pipe(
                map((response) => {
                    this._reportStatus.next(response.result);
                    return response;
                }),
                catchError((error) => {
                    return of(error.error);
                })
            );
    }

    generateReport(generateReport: IGenerateReport): Observable<any> {
        const httpOptions: Object = {
            responseType: 'blob',
            observe: 'response',
        };
        return this._httpClient
            .post<any>(
                `${environment.baseUrl}/Report/generate`,
                generateReport,
                httpOptions
            )
            .pipe(
                tap((response) => {
                    return response;
                })
            );
    }

    copyReport(copyreport: CopyReport): Observable<BaseRespone<CopyReport>> {
        return this._httpClient
            .post<BaseRespone<CopyReport>>(
                `${environment.baseUrl}/Report/CopyReport`,
                copyreport
            )
            .pipe(
                catchError((error) => {
                    return of(error.error);
                }),
                map((newProduct) => {
                    // Return the new product
                    return newProduct;
                })
            );
    }
    multipleReportStatusChange(
        multiplestatusChange: IInwardStatusChange
    ): Observable<BaseRespone<IInwardStatusChange>> {
        return this._httpClient
            .put<BaseRespone<IInwardStatusChange>>(
                `${environment.baseUrl}/Inward/Multiple/StatusChange`,
                multiplestatusChange
            )
            .pipe(
                map((response) => {
                    this._multipleReportStatus.next(response.result);
                    return response;
                }),
                catchError((error) => {
                    return of(error.error);
                })
            );
    }

    feesChangeRequest(
        outward: IOutwardInward
    ): Observable<BaseRespone<IOutwardInward>> {
        return this._httpClient
            .post<BaseRespone<IOutwardInward>>(
                `${environment.baseUrl}/Outward/FeesChangeRequest`,
                outward
            )
            .pipe(
                catchError((error) => {
                    return of(error.error);
                }),
                map((newProduct) => {
                    return newProduct;
                })
            );
    }
}
